import ExportButton from '../../../components/ExportButton';
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import Breadcrumbs from "../../../components/Breadcrumbs";
import CustomTabs from "../../../components/CustomTabs";
import NewTableComponent from "../../../components/NewTableComponent";
import TablePopover from "../../../components/TablePopover";
import UserAvatar from "../../../components/UserAvatar";
import MultiLineText from "../../../components/MultiLineText";
import DeleteModal from "../../../components/modals/DeleteModal";
import { activeRFQ, draftIcon, plusIcon, PO, POActive, RFQ, RFQActive, RFQIcon, totalBidIcon, coinsStackIcon, partValueIcon,
 } from "../../../utils/svgs";
import Stats from "../../../components/Stats";
import "../../../styles/Procurement.css";
import PermissionWrapper from '../../../components/PermissionWrapper';
import { AppMainModules, InventorySubModules, ProcurementPermission } from '../../../utils/app-constant';
import { useDispatch, useSelector } from "react-redux";
import { fetchRFQsPaginated, fetchRFQOverview, updatePartRFQStatus, updateRFQStatusReducer, updateSupplyChainStatus, updateRFQSupplyChainStatusReducer, createPurchaseOrder, fetchPurchaseOrdersPaginated, fetchPurchaseOrdersOverview, setRfqState, setPoState, deletePartRFQ, deletePurchaseOrder } from "../../../store/Slices/ProcurementSlice";
import { getVendorsDropdown } from "../../../store/Slices/WorkOrderSlice";
import { successNotify, errorNotify } from "../../../utils/toast";
import { apiService } from "../../../services/apiService";
import DateFilterDropdown from "../../../components/DateFilterDropdown";
import { hasPermission } from "../../../utils/helper";
import moment from "moment";
import { BsFileText, BsArrowRepeat, BsArrowUp, BsSlashCircle, BsClock, BsCheckCircleFill, BsXCircleFill, BsTruck } from "react-icons/bs";
import { IoMdArrowDropright } from "react-icons/io";
import { PiTrash } from "react-icons/pi";
import unpaidStatusIcon from "../../../assets/images/unpaid.svg";
import partialPaidStatusIcon from "../../../assets/images/partial.svg";
import fullyPaidStatusIcon from "../../../assets/images/fully_paid.svg";
import { getBidIdentifier, resolveBidPoActionState, toIdKey } from "./bidPoStatus";

const APP_MODULE = AppMainModules.inventoryManagement;
const PROCUREMENT_MODULE = InventorySubModules.Procurement;
const RFQ_TAB = "RFQ";
const PO_TAB = "PO";
const LIST_FETCH_DEBOUNCE_MS = 500;
const TABLE_DATE_FORMAT = "YYYY-MM-DD";
const DEFAULT_STATUS_POPUP_STATE = { open: false, rowId: null, anchorPos: null };
const PO_STATUS_FILTER_OPTIONS = [
    { label: "New", value: "new" },
    { label: "Pending", value: "pending" },
    { label: "Shipped", value: "shipped" },
    { label: "Partially Received", value: "partially received" },
    { label: "Completed", value: "completed" },
];
const PO_PAYMENT_FILTER_OPTIONS = [
    { label: "Unpaid", value: "unpaid" },
    { label: "Partial Paid", value: "partial_paid" },
    { label: "Fully Paid", value: "fully_paid" },
];
const RFQ_STATUS_FILTER_OPTIONS = [
    { label: "Draft", value: "draft" },
    { label: "Bidding", value: "biding" },
    { label: "Closed", value: "closed" },
];
const PartRfqSupplyChainStatusEnum = Object.freeze({
    Pending: "pending",
    Approved: "approved",
    Rejected: "rejected",
});
const RFQ_APPROVAL_FILTER_OPTIONS = [
    { label: "Pending", value: PartRfqSupplyChainStatusEnum.Pending },
    { label: "Approved", value: PartRfqSupplyChainStatusEnum.Approved },
    { label: "Rejected", value: PartRfqSupplyChainStatusEnum.Rejected },
];

const normalizeRfqStatus = (status) => {
    const value = String(status || "").toLowerCase().trim();
    if (value === "bidding" || value === "biding") return "biding";
    if (value === "close" || value === "closed") return "closed";
    return value;
};

const getRfqStatusMeta = (status) => {
    const normalized = normalizeRfqStatus(status);
    const defaultMeta = {
        value: normalized,
        label: "-",
        classKey: normalized,
        color: "#4D4D4D",
        icon: BsFileText,
    };
    if (!normalized) return defaultMeta;

    const statusMap = {
        draft: {
            value: "draft",
            label: "Draft",
            classKey: "draft",
            color: "#4D4D4D",
            icon: BsFileText,
        },
        biding: {
            value: "biding",
            label: "Bidding",
            classKey: "bidding",
            color: "#4014B8",
            icon: BsArrowUp,
        },
        completed: {
            value: "completed",
            label: "Completed",
            classKey: "completed",
            color: "#10B981",
            icon: BsCheckCircleFill,
        },
        closed: {
            value: "closed",
            label: "Closed",
            classKey: "closed",
            color: "#3D3F5C",
            icon: BsSlashCircle,
        },
    };

    return statusMap[normalized] || {
        ...defaultMeta,
        label: normalized.charAt(0).toUpperCase() + normalized.slice(1),
    };
};

const normalizePaymentStatus = (status) => {
    const value = String(status || "")
        .toLowerCase()
        .trim()
        .replace(/[\s-]+/g, "_");

    if (!value) return "";
    if (value === "partial" || value === "partially" || value === "partially_paid") return "partial_paid";
    if (value === "fully" || value === "fullypaid") return "fully_paid";
    if (value === "un_paid") return "unpaid";
    return value;
};

const PAYMENT_STATUS_META = {
    unpaid: {
        label: "Unpaid",
        color: "#3D3F5C",
        icon: unpaidStatusIcon,
    },
    partial_paid: {
        label: "Partial Paid",
        color: "#4014B8",
        icon: partialPaidStatusIcon,
    },
    fully_paid: {
        label: "Fully Paid",
        color: "#00BA37",
        icon: fullyPaidStatusIcon,
    },
};

const getPaymentStatusMeta = (status) => {
    const normalized = normalizePaymentStatus(status);
    const mapped = PAYMENT_STATUS_META[normalized];
    if (mapped) return mapped;
    if (!normalized) return { label: "-", color: "#4D4D4D", icon: null };

    const fallbackLabel = normalized
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

    return {
        label: fallbackLabel,
        color: "#4D4D4D",
        icon: null,
    };
};

const normalizePoStatus = (status) => {
    const normalized = String(status || "").toLowerCase().trim();
    if (normalized === "new") return "pending";
    return normalized;
};

const getPoStatusMeta = (status) => {
    const normalized = normalizePoStatus(status);
    const statusMap = {
        pending: { label: "Pending", color: "#F59E0B", icon: BsClock },
        shipped: { label: "Shipped", color: "#3B82F6", icon: BsTruck },
        "partially received": { label: "Partially Received", color: "#14B8A6", icon: BsArrowRepeat },
        completed: { label: "Completed", color: "#10B981", icon: BsCheckCircleFill },
    };
    return statusMap[normalized] || {
        label: status || "-",
        color: "#4D4D4D",
        icon: BsClock,
    };
};

const getRfqApprovalMeta = (status) => {
    const normalizedStatus = normalizeRfqStatus(status);

    if (normalizedStatus === "approved") {
        return { label: "Approved", classKey: "approved", color: "#10B981", icon: BsCheckCircleFill };
    }

    if (normalizedStatus === "rejected" || normalizedStatus === "cancelled") {
        return { label: "Rejected", classKey: "rejected", color: "#EF4444", icon: BsXCircleFill };
    }

    return { label: "Pending", classKey: "pending", color: "#F59E0B", icon: BsClock };
};

const isSupplyChainApproved = (status) => {
    const normalizedStatus = normalizeRfqStatus(status);
    return normalizedStatus === PartRfqSupplyChainStatusEnum.Approved;
};

const formatDateCell = (dateValue) => {
    if (!dateValue) return "-";
    const parsed = moment(dateValue);
    return parsed.isValid() ? parsed.format(TABLE_DATE_FORMAT) : "-";
};

const formatAmountCell = (value, fallback = "0") => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return fallback;
    return numericValue.toLocaleString();
};

const formatStatValue = (value) => {
    const numericValue = Number(value || 0);
    if (!Number.isFinite(numericValue)) return 0;
    if (Math.abs(numericValue) >= 1000000) {
        return `${(numericValue / 1000000).toFixed(1)}M`;
    }
    return numericValue.toLocaleString();
};

const toFiniteNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const roundAmount = (value) => {
    const numericValue = toFiniteNumber(value);
    if (numericValue === null) return 0;
    return Number(numericValue.toFixed(2));
};

const getLinkedQuote = (entity) =>
    entity?.vendor_quote || entity?.rfq_vendor_quote || entity?.quote || null;

const resolveLinkedQuoteTotal = (entity) => {
    const linkedQuote = getLinkedQuote(entity);
    if (!linkedQuote) return null;

    const explicitTotal = toFiniteNumber(linkedQuote?.total_amount ?? linkedQuote?.total_payable);
    if (explicitTotal !== null) return roundAmount(explicitTotal);

    const subTotal = toFiniteNumber(linkedQuote?.sub_total ?? entity?.sub_total);
    const totalShipping = toFiniteNumber(
        linkedQuote?.total_shipping ?? linkedQuote?.shipping ?? entity?.total_shipping
    );
    const taxPercent = toFiniteNumber(
        linkedQuote?.total_tax ?? linkedQuote?.tax ?? entity?.total_tax
    );
    const discountPercent = toFiniteNumber(
        linkedQuote?.total_discount ?? linkedQuote?.discount ?? entity?.total_discount
    );

    if (subTotal === null && totalShipping === null && taxPercent === null && discountPercent === null) {
        return null;
    }

    const safeSubTotal = subTotal ?? 0;
    const safeShipping = totalShipping ?? 0;
    const safeTaxPercent = taxPercent ?? 0;
    const safeDiscountPercent = discountPercent ?? 0;

    const totalTax = (safeSubTotal * safeTaxPercent) / 100;
    const totalDiscount = (safeSubTotal * safeDiscountPercent) / 100;
    return roundAmount(safeSubTotal + safeShipping + totalTax - totalDiscount);
};

const resolvePoTotalAmount = (row) => {
    const payableTotal = toFiniteNumber(row?.total_payable ?? row?.total_amount);
    const linkedQuoteTotal = resolveLinkedQuoteTotal(row);

    if (
        linkedQuoteTotal !== null &&
        linkedQuoteTotal > 0 &&
        (payableTotal === null || Math.abs(payableTotal - linkedQuoteTotal) > 0.01)
    ) {
        return linkedQuoteTotal;
    }

    return payableTotal;
};

const Procurement = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [statusPopup, setStatusPopup] = useState(DEFAULT_STATUS_POPUP_STATE);
    const statusPopupRef = useRef();
    const [approvalPopup, setApprovalPopup] = useState(DEFAULT_STATUS_POPUP_STATE);
    const approvalPopupRef = useRef();
    const [loadingPOs, setLoadingPOs] = useState({});
    const [generatedPoBidIds, setGeneratedPoBidIds] = useState({});
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ type: null, row: null });

    const activeTabParam = String(searchParams.get("tab") || RFQ_TAB).toUpperCase();
    const activeTab = activeTabParam === PO_TAB ? PO_TAB : RFQ_TAB;
    const isRfqTab = activeTab === RFQ_TAB;

    const {
        getRFQsPaginated,
        getRFQsLoading,
        getRFQOverviewData,
        getPurchaseOrdersPaginated,
        getPurchaseOrdersLoading,
        getPurchaseOrdersOverviewData,
        deletePartRFQLoading,
        deletePurchaseOrderLoading,
        rfqState,
        poState
    } = useSelector((state) => state.procurement || {});

    const { page: rfqPage = 1, limit: rfqLimit = 10, searchTerm: rfqSearch = "", searchFilters: rfqFilters = {} } = rfqState || {};
    const { page: poPage = 1, limit: poLimit = 10, searchTerm: poSearch = "", searchFilters: poFilters = {} } = poState || {};

    const currentPage = isRfqTab ? rfqPage : poPage;
    const limit = isRfqTab ? rfqLimit : poLimit;
    const searchTerm = isRfqTab ? rfqSearch : poSearch;
    const searchFilters = isRfqTab ? rfqFilters : poFilters;

    const canViewRfq = hasPermission(PROCUREMENT_MODULE, ProcurementPermission.ViewRfq, APP_MODULE);
    const canViewPo = hasPermission(PROCUREMENT_MODULE, ProcurementPermission.ViewPo, APP_MODULE);
    const canCreateRfq = hasPermission(PROCUREMENT_MODULE, ProcurementPermission.CreateRfq, APP_MODULE);
    const canUpdateRfq = hasPermission(PROCUREMENT_MODULE, ProcurementPermission.UpdateRfq, APP_MODULE);
    const canApproveSupplyChain = hasPermission(PROCUREMENT_MODULE, ProcurementPermission.RfqSupplyChainApproval, APP_MODULE);
    const canCreatePo = hasPermission(PROCUREMENT_MODULE, ProcurementPermission.CreatePo, APP_MODULE);

    const setActiveTab = useCallback((tab) => {
        setSearchParams({ tab });
    }, [setSearchParams]);

    const updateTabState = useCallback((tab, patch) => {
        if (tab === RFQ_TAB) {
            dispatch(setRfqState(patch));
            return;
        }
        dispatch(setPoState(patch));
    }, [dispatch]);

    const setCurrentPage = useCallback((page) => {
        updateTabState(activeTab, { page });
    }, [activeTab, updateTabState]);

    const setLimit = useCallback((nextLimit) => {
        updateTabState(activeTab, { limit: nextLimit, page: 1 });
    }, [activeTab, updateTabState]);

    const setSearchTerm = useCallback((term) => {
        updateTabState(activeTab, { searchTerm: term, page: 1 });
    }, [activeTab, updateTabState]);

    const setSearchFilters = useCallback((valueOrFactory) => {
        const currentFilters = isRfqTab ? rfqFilters : poFilters;
        const nextFilters = typeof valueOrFactory === "function"
            ? valueOrFactory(currentFilters)
            : valueOrFactory;

        updateTabState(activeTab, { searchFilters: nextFilters || {}, page: 1 });
    }, [activeTab, isRfqTab, poFilters, rfqFilters, updateTabState]);

    const listQueryPayload = useMemo(() => ({
        page: currentPage,
        limit,
        search_text: searchTerm,
        searchFilters,
    }), [currentPage, limit, searchTerm, searchFilters]);

    const rfqQueryPayload = useMemo(() => ({
        page: rfqPage,
        limit: rfqLimit,
        search_text: rfqSearch,
        searchFilters: rfqFilters,
    }), [rfqFilters, rfqLimit, rfqPage, rfqSearch]);

    const poQueryPayload = useMemo(() => ({
        page: poPage,
        limit: poLimit,
        search_text: poSearch,
        searchFilters: poFilters,
    }), [poFilters, poLimit, poPage, poSearch]);

    useEffect(() => {
        dispatch(fetchRFQOverview());
        dispatch(fetchPurchaseOrdersOverview());
        dispatch(getVendorsDropdown());
    }, [dispatch]);



    const rfqList = getRFQsPaginated?.results || [];
    const meta = getRFQsPaginated?.meta || {};
    const totalRfqItems = meta.totalItems || 0;
    const totalRfqPages = meta.totalPages || 1;

    const fetchPaginatedData = useCallback(() => {
        if (activeTab === RFQ_TAB) {
            dispatch(fetchRFQsPaginated(listQueryPayload));
            return;
        }
        dispatch(fetchPurchaseOrdersPaginated(listQueryPayload));
    }, [activeTab, dispatch, listQueryPayload]);

    const refreshRfqTable = useCallback(() => {
        dispatch(fetchRFQsPaginated(rfqQueryPayload));
        dispatch(fetchRFQOverview());
    }, [dispatch, rfqQueryPayload]);

    const refreshPoTable = useCallback(() => {
        dispatch(fetchPurchaseOrdersPaginated(poQueryPayload));
        dispatch(fetchPurchaseOrdersOverview());
    }, [dispatch, poQueryPayload]);

    const [rfqPdfExportLoading, setRfqPdfExportLoading] = useState(false);
    const [rfqExcelExportLoading, setRfqExcelExportLoading] = useState(false);
    const [poPdfExportLoading, setPoPdfExportLoading] = useState(false);
    const [poExcelExportLoading, setPoExcelExportLoading] = useState(false);

    const hasActiveRfqFilters = useMemo(() => {
        if (rfqSearch && rfqSearch.trim().length > 0) return true;
        if (!rfqFilters || typeof rfqFilters !== "object") return false;
        return Object.values(rfqFilters).some(val => val !== undefined && val !== null && val !== "" && (typeof val !== "object" || Object.keys(val).length > 0));
    }, [rfqFilters, rfqSearch]);

    const hasActivePoFilters = useMemo(() => {
        if (poSearch && poSearch.trim().length > 0) return true;
        if (!poFilters || typeof poFilters !== "object") return false;
        return Object.values(poFilters).some(val => val !== undefined && val !== null && val !== "" && (typeof val !== "object" || Object.keys(val).length > 0));
    }, [poFilters, poSearch]);

    const buildExportPayload = (filters, search) => {
        const payload = search ? { search_text: search } : {};
        if (filters && typeof filters === "object") {
            Object.assign(payload, filters);
        }
        return payload;
    };

    const triggerFileDownload = (filePath, defaultName) => {
        if (!filePath) {
            throw new Error("File path was not returned from the server");
        }
        const fileUrl = /^https?:\/\//i.test(filePath) ? filePath : `${process.env.REACT_APP_API_FILES_URL || ""}${filePath}`;
        const link = document.createElement("a");
        link.href = fileUrl;
        link.target = "_blank";
        link.setAttribute("download", defaultName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleRfqExportPdf = async () => {
        if (!hasActiveRfqFilters || rfqPdfExportLoading) return;
        setRfqPdfExportLoading(true);
        try {
            const payload = buildExportPayload(rfqFilters, rfqSearch);
            const response = await apiService.getPartsRfqListingPdf(payload);
            const url = response?.data?.url || response?.url;
            triggerFileDownload(url, "rfq-listing.pdf");
        } catch (err) {
            errorNotify(err?.message || err || "Failed to download RFQ PDF");
        } finally {
            setRfqPdfExportLoading(false);
        }
    };

    const handleRfqExportExcel = async () => {
        if (!hasActiveRfqFilters || rfqExcelExportLoading) return;
        setRfqExcelExportLoading(true);
        try {
            const payload = buildExportPayload(rfqFilters, rfqSearch);
            const response = await apiService.getPartsRfqListingExcel(payload);
            const url = response?.data?.url || response?.url;
            triggerFileDownload(url, "rfq-listing.xlsx");
        } catch (err) {
            errorNotify(err?.message || err || "Failed to download RFQ Excel");
        } finally {
            setRfqExcelExportLoading(false);
        }
    };

    const handlePoExportPdf = async () => {
        if (!hasActivePoFilters || poPdfExportLoading) return;
        setPoPdfExportLoading(true);
        try {
            const payload = buildExportPayload(poFilters, poSearch);
            const response = await apiService.getPartsPurchaseOrdersListingPdf(payload);
            const url = response?.data?.url || response?.url;
            triggerFileDownload(url, "purchase-orders-listing.pdf");
        } catch (err) {
            errorNotify(err?.message || err || "Failed to download PO PDF");
        } finally {
            setPoPdfExportLoading(false);
        }
    };

    const handlePoExportExcel = async () => {
        if (!hasActivePoFilters || poExcelExportLoading) return;
        setPoExcelExportLoading(true);
        try {
            const payload = buildExportPayload(poFilters, poSearch);
            const response = await apiService.getPartsPurchaseOrdersListingExcel(payload);
            const url = response?.data?.url || response?.url;
            triggerFileDownload(url, "purchase-orders-listing.xlsx");
        } catch (err) {
            errorNotify(err?.message || err || "Failed to download PO Excel");
        } finally {
            setPoExcelExportLoading(false);
        }
    };

    const closeDeleteModal = useCallback(() => {
        setDeleteModalOpen(false);
        setDeleteTarget({ type: null, row: null });
    }, []);

    const openDeleteModal = useCallback((type, row) => {
        setDeleteTarget({ type, row });
        setDeleteModalOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        const targetId = deleteTarget?.row?.id;
        if (!targetId) {
            errorNotify("Unable to determine record for deletion.");
            return;
        }

        try {
            if (deleteTarget.type === RFQ_TAB) {
                await dispatch(deletePartRFQ(targetId)).unwrap();
                successNotify("RFQ deleted successfully");
                refreshRfqTable();
            } else if (deleteTarget.type === PO_TAB) {
                await dispatch(deletePurchaseOrder(targetId)).unwrap();
                successNotify("Purchase Order deleted successfully");
                refreshPoTable();
            }
            closeDeleteModal();
        } catch (error) {
            errorNotify(error);
        }
    }, [closeDeleteModal, deleteTarget?.row?.id, deleteTarget.type, dispatch, refreshPoTable, refreshRfqTable]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchPaginatedData();
        }, LIST_FETCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [fetchPaginatedData]);

    const closeStatusPopup = useCallback(() => {
        setStatusPopup(DEFAULT_STATUS_POPUP_STATE);
    }, []);
    const closeApprovalPopup = useCallback(() => {
        setApprovalPopup(DEFAULT_STATUS_POPUP_STATE);
    }, []);

    useEffect(() => {
        if (!statusPopup.open) return undefined;

        const handleClickOutside = (event) => {
            if (event.target.closest(".rfq_status_trigger")) {
                return;
            }
            if (statusPopupRef.current && !statusPopupRef.current.contains(event.target)) {
                closeStatusPopup();
            }
        };

        const handleScroll = () => {
            closeStatusPopup();
        };

        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScroll, true);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [closeStatusPopup, statusPopup.open]);

    useEffect(() => {
        if (!canApproveSupplyChain) {
            setApprovalPopup(DEFAULT_STATUS_POPUP_STATE);
            return undefined;
        }
        if (!approvalPopup.open) return undefined;

        const handleClickOutside = (event) => {
            if (event.target.closest(".rfq_approval_trigger")) {
                return;
            }
            if (approvalPopupRef.current && !approvalPopupRef.current.contains(event.target)) {
                closeApprovalPopup();
            }
        };

        const handleScroll = () => {
            closeApprovalPopup();
        };

        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScroll, true);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [approvalPopup.open, canApproveSupplyChain, closeApprovalPopup]);

    const handleStatusClick = useCallback((event, row) => {
        const currentStatusMeta = getRfqStatusMeta(row?.status);
        const isApprovedBySupplyChain = isSupplyChainApproved(row?.supply_chain_status);
        if (currentStatusMeta.value === "closed" || !isApprovedBySupplyChain || !canUpdateRfq) return;

        event.stopPropagation();
        closeApprovalPopup();
        const rect = event.currentTarget.getBoundingClientRect();
        setStatusPopup((prev) => {
            if (prev.open && String(prev.rowId) === String(row?.id)) {
                return DEFAULT_STATUS_POPUP_STATE;
            }
            return {
                open: true,
                rowId: row.id,
                anchorPos: {
                    left: rect.left,
                    right: rect.right,
                    top: rect.top,
                    bottom: rect.bottom,
                    x: rect.left,
                    y: rect.top,
                },
            };
        });
    }, [canUpdateRfq, closeApprovalPopup]);

    const handleStatusSelect = useCallback((newStatus, row) => {
        if (!isSupplyChainApproved(row?.supply_chain_status)) {
            closeStatusPopup();
            errorNotify("Current status can only be changed after supply chain approval is approved");
            return;
        }

        const nextStatus = normalizeRfqStatus(newStatus);
        const currentStatus = normalizeRfqStatus(row.status);
        if (nextStatus === currentStatus) return;

        closeStatusPopup();

        dispatch(updatePartRFQStatus({ part_rfq_id: Number(row.id), status: nextStatus }))
            .unwrap()
            .then(() => {
                dispatch(updateRFQStatusReducer({ id: row.id, status: nextStatus }));
                refreshRfqTable();
                successNotify("Status Updated Successfully");
            })
            .catch((err) => {
                errorNotify(err);
            });
    }, [closeStatusPopup, dispatch, refreshRfqTable]);

    const handleSupplyApprovalClick = useCallback((event, row) => {
        if (!canApproveSupplyChain) {
            closeApprovalPopup();
            return;
        }

        event.stopPropagation();
        closeStatusPopup();
        const rect = event.currentTarget.getBoundingClientRect();
        setApprovalPopup((prev) => {
            if (prev.open && String(prev.rowId) === String(row?.id)) {
                return DEFAULT_STATUS_POPUP_STATE;
            }
            return {
                open: true,
                rowId: row.id,
                anchorPos: {
                    left: rect.left,
                    right: rect.right,
                    top: rect.top,
                    bottom: rect.bottom,
                    x: rect.left,
                    y: rect.top,
                },
            };
        });
    }, [canApproveSupplyChain, closeApprovalPopup, closeStatusPopup]);

    const handleSupplyApprovalSelect = useCallback((newStatus, row) => {
        if (!canApproveSupplyChain) {
            closeApprovalPopup();
            return;
        }
        const nextStatus = normalizeRfqStatus(newStatus);
        const currentStatus = normalizeRfqStatus(row.supply_chain_status);
        if (nextStatus === currentStatus) return;

        closeApprovalPopup();
        dispatch(updateSupplyChainStatus({ part_rfq_id: Number(row.id), supply_chain_status: nextStatus }))
            .unwrap()
            .then(() => {
                dispatch(updateRFQSupplyChainStatusReducer({ id: row.id, supply_chain_status: nextStatus }));
                refreshRfqTable();
                successNotify("Supply Chain Status Updated Successfully");
            })
            .catch((err) => {
                errorNotify(err);
            });
    }, [canApproveSupplyChain, closeApprovalPopup, dispatch, refreshRfqTable]);

    const StatusPopup = ({ row, onSelect }) => {
        const options = [
            { value: 'draft', label: 'Draft', icon: <BsFileText />, color: '#4D4D4D' },
            { value: 'biding', label: 'Bidding', icon: <BsArrowUp />, color: '#4014B8' }
        ];

        return (
            <TablePopover
                innerRef={statusPopupRef}
                anchorRect={statusPopup.anchorPos}
                popupHeight={85}
                popupWidth={150}
                style={{ minWidth: "150px" }}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: "flex", flexDirection: "column", gap: "2px", padding: "4px 0" }}
                >
                    {options.map((opt) => (
                        <div key={opt.value} onClick={() => onSelect(opt.value, row)}
                            style={{
                                display: "flex",
                                alignItems: 'center',
                                padding: "8px 12px",
                                gap: "10px",
                                cursor: "pointer",
                                backgroundColor: normalizeRfqStatus(row.status) === normalizeRfqStatus(opt.value) ? 'rgb(156 159 201 / 15%)' : "transparent",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = normalizeRfqStatus(row.status) === normalizeRfqStatus(opt.value) ? 'rgb(156 159 201 / 15%)' : "transparent"}
                        >
                            <span style={{ color: opt.color, display: "flex", fontSize: "16px" }}>{opt.icon}</span>
                            <span style={{ fontSize: "14px", color: "#3D3F5C", textTransform: 'capitalize', fontWeight: 500 }}>{opt.label}</span>
                        </div>
                    ))}
                </div>
            </TablePopover>
        );
    };

    const ApprovalPopup = ({ row, onSelect }) => {
        const options = [
            { value: PartRfqSupplyChainStatusEnum.Approved, label: "Approve", icon: <BsCheckCircleFill />, color: "#10B981" },
            { value: PartRfqSupplyChainStatusEnum.Rejected, label: "Reject", icon: <BsXCircleFill />, color: "#EF4444" },
        ];

        return (
            <TablePopover
                innerRef={approvalPopupRef}
                anchorRect={approvalPopup.anchorPos}
                popupHeight={85}
                popupWidth={160}
                style={{ minWidth: "160px" }}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: "flex", flexDirection: "column", gap: "2px", padding: "4px 0" }}
                >
                    {options.map((opt) => (
                        <div
                            key={opt.value}
                            onClick={() => onSelect(opt.value, row)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "8px 12px",
                                gap: "10px",
                                cursor: "pointer",
                                backgroundColor: normalizeRfqStatus(row.supply_chain_status) === normalizeRfqStatus(opt.value) ? "rgb(156 159 201 / 15%)" : "transparent",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.05)")}
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                    normalizeRfqStatus(row.supply_chain_status) === normalizeRfqStatus(opt.value) ? "rgb(156 159 201 / 15%)" : "transparent")
                            }
                        >
                            <span style={{ color: opt.color, display: "flex", fontSize: "16px" }}>{opt.icon}</span>
                            <span style={{ fontSize: "14px", color: "#3D3F5C", textTransform: "capitalize", fontWeight: 500 }}>{opt.label}</span>
                        </div>
                    ))}
                </div>
            </TablePopover>
        );
    };

    const handleGeneratePO = useCallback((bid) => {
        setLoadingPOs(prev => ({ ...prev, [bid.id]: true }));
        const payload = {
            parts_rfq_id: bid.parts_rfq_id,
            vendor_id: bid.vendor_id,
            rfq_vendor_quote_id: bid.id
        };

        dispatch(createPurchaseOrder(payload))
            .unwrap()
            .then(() => {
                successNotify("Purchase Order Generated Successfully");
                const rfqId = toIdKey(bid.parts_rfq_id);
                const bidId = getBidIdentifier(bid);
                if (rfqId && bidId) {
                    setGeneratedPoBidIds((prev) => ({ ...prev, [rfqId]: bidId }));
                }
                dispatch(fetchRFQsPaginated(listQueryPayload));
            })
            .catch((err) => {
                errorNotify(err);
            })
            .finally(() => {
                setLoadingPOs(prev => ({ ...prev, [bid.id]: false }));
            });
    }, [dispatch, listQueryPayload]);

    const ExpandableBidTable = ({ data }) => {
        if (!data?.bids || data.bids.length === 0) {
            return null;
        }

        return (
            <div style={{ padding: '20px 24px', backgroundColor: '#fff', borderTop: '1px solid #E2E8F0' }} onClick={(e) => e.stopPropagation()}>
                <h5 style={{ fontSize: '16px', fontWeight: 600, color: '#1E293B', marginBottom: '16px' }}>Received Bids</h5>
                <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ backgroundColor: '#F8FAFC' }}>
                            <tr>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Vendor Name</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Delivery Type</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Delivery Date</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Discount</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Freight (PKR)</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Taxes (PKR)</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Total Quote (PKR)</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Generate PO</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.bids.map((bid, index) => {
                                const rfqId = toIdKey(data?.id);
                                const actionState = resolveBidPoActionState({
                                    bid,
                                    bids: data.bids,
                                    rfqPoGenerated: data.po_generated,
                                    generatedBidId: rfqId ? generatedPoBidIds[rfqId] : null,
                                    container: data,
                                });
                                const isLoading = Boolean(loadingPOs[bid.id]);
                                const isDisabled = isLoading || actionState.disabled;
                                const actionColor = actionState.intent === "dismissed" ? "#64748B" : "#4014B8";

                                return (
                                    <tr key={bid.id} style={{ borderTop: index > 0 ? '1px solid #E2E8F0' : 'none' }}>
                                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#334155' }}>{bid.vendor?.name || '-'}</td>
                                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#334155', textTransform: 'capitalize' }}>{bid.delivery_type || '-'}</td>
                                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#334155' }}>{formatDateCell(bid.delivery_date)}</td>
                                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#334155' }}>{Number(bid.total_discount) > 0 ? `${Number(bid.total_discount)}%` : '0%'}</td>
                                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#334155' }}>{formatAmountCell(bid.total_shipping)}</td>
                                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#334155' }}>{formatAmountCell(bid.total_tax)}</td>
                                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#334155' }}>{formatAmountCell(bid.total_amount)}</td>
                                        <td style={{ padding: '12px 16px' }}>
                                            <PermissionWrapper
                                                module={PROCUREMENT_MODULE}
                                                permission={ProcurementPermission.CreatePo}
                                                mainModule={APP_MODULE}
                                            >
                                                <button
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        padding: '6px 12px',
                                                        backgroundColor: '#fff',
                                                        border: '1px solid #E2E8F0',
                                                        borderRadius: '6px',
                                                        color: actionColor,
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                        opacity: isDisabled ? 0.7 : 1
                                                    }}
                                                    onClick={() => handleGeneratePO(bid)}
                                                    disabled={isDisabled}
                                                >
                                                    {isLoading ? <Spinner size="sm" animation="border" role="status" /> : <BsFileText />}
                                                    {isLoading ? 'Generating...' : actionState.label}
                                                </button>
                                            </PermissionWrapper>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const tripsTabs = [
        {
            key: RFQ_TAB,
            label: "Request for Quotations (RFQs)",
            icon: activeTab === RFQ_TAB ? RFQActive : RFQ,
            enabled: canViewRfq,
        },
        {
            key: PO_TAB,
            label: "Purchase Orders (POs)",
            icon: activeTab === PO_TAB ? POActive : PO,
            enabled: canViewPo,
        }
    ].filter((tab) => tab.enabled);

    useEffect(() => {
        if (tripsTabs.length > 0 && !tripsTabs.find(t => t.key === activeTab)) {
            setActiveTab(tripsTabs[0].key);
        }
    }, [activeTab, setActiveTab, tripsTabs]);

    const procurementStatsData = useMemo(() => [
        {
            title: "Active RFQs",
            value: getRFQOverviewData?.active_rfqs || 0,
            text: "RFQs in progress",
            icon: activeRFQ,
        },
        {
            title: "Total Bids Received",
            value: getRFQOverviewData?.total_rfq_bids || 0,
            text: "Submitted by vendors",
            icon: totalBidIcon,
        },
        {
            title: "Drafts",
            value: getRFQOverviewData?.draft_rfq || 0,
            text: "Saved but not issued",
            icon: draftIcon,
        },
        {
            title: "Total Closed RFQs",
            value: getRFQOverviewData?.closed_rfq || 0,
            text: "Successfully closed RFQs",
            icon: RFQIcon,
        },
    ], [getRFQOverviewData]);

    const poOverviewData = getPurchaseOrdersOverviewData?.overview || getPurchaseOrdersOverviewData || {};

    const poStatsData = [
        {
            title: "Average Procurement Cost",
            value: formatStatValue(
                poOverviewData?.average_procurement_cost ??
                poOverviewData?.average_purchase_order_cost ??
                poOverviewData?.avg_cost_per_purchase_order ??
                0
            ),
            text: "Avg cost per purchase order",
            icon: coinsStackIcon,
        },
        {
            title: "Outstanding Amount",
            value: formatStatValue(
                poOverviewData?.outstanding_amount ??
                poOverviewData?.pending_vendor_payments ??
                poOverviewData?.outstanding_total ??
                0
            ),
            text: "Pending vendor payments",
            icon: totalBidIcon,
            className: "completed",
        },
        {
            title: "Procurement Value",
            value: formatStatValue(
                poOverviewData?.procurement_value ??
                poOverviewData?.total_procurement_value ??
                poOverviewData?.total_spend_recorded ??
                0
            ),
            text: "Total spend recorded",
            icon: partValueIcon,
            className: "late",
        },
        {
            title: "Pending Deliveries",
            value: formatStatValue(
                poOverviewData?.pending_deliveries ??
                poOverviewData?.awaiting_delivery_completion ??
                poOverviewData?.pending_deliveries_total ??
                0
            ),
            text: "Awaiting delivery completion",
            icon: RFQIcon,
            className: "pending-delivery",
        },
    ];

    const poList = getPurchaseOrdersPaginated?.results || [];
    const poMeta = getPurchaseOrdersPaginated?.meta || {};
    const totalPoItems = poMeta.totalItems || 0;
    const totalPoPages = poMeta.totalPages || 1;

    const poColumns = useMemo(() => ([
        {
            name: "PO ID",
            selector: (row) => row.parts_purchase_order_id,
            cell: (row) => (
                canViewPo
                    ? <span className="rfq_link" onClick={() => navigate(`/po/details/${row.id}`)}>{row.parts_purchase_order_id}</span>
                    : <span>{row.parts_purchase_order_id}</span>
            ),
            sortable: false,
            tableColumn: true,
            filterName: "partsPurchaseOrderIdFilter",
            type: "text",
            width: "140px",
        },
        {
            name: "Vendor Name",
            selector: (row) => row.vendor?.name,
            sortable: false,
            tableColumn: true,
            filterName: "vendorFilter",
            type: "text",
            width: "150px",
        },
        {
            name: "Total Amount",
            selector: (row) => formatAmountCell(resolvePoTotalAmount(row), "-"),
            sortable: false,
            tableColumn: true,
            filterName: "totalAmountFilter",
            type: "text",
            width: "140px",
        },
        {
            name: "Expected Delivery Date",
            selector: (row) => formatDateCell(row.rfq?.target_delivery_date),
            sortable: false,
            tableColumn: true,
            filterName: "expectDateFilter",
            type: "select",
            filterComponent: DateFilterDropdown,
            width: "180px",
        },
        {
            name: "Linked RFQ",
            selector: (row) => row.rfq?.parts_rfq_id,
            cell: (row) => (
                <span className="rfq_link" style={{ textDecoration: 'none', color: '#4014B8' }} onClick={() => navigate(`/rfqs/details/${row.rfq?.id}`)}>{row.rfq?.parts_rfq_id}</span>
            ),
            sortable: false,
            tableColumn: true,
            filterName: "partsRfqIdFilter",
            type: "text",
            width: "140px",
        },
        {
            name: "Delivery Status",
            selector: (row) => normalizePoStatus(row.status),
            cell: (row) => {
                const statusMeta = getPoStatusMeta(row.status);
                const StatusIcon = statusMeta.icon;

                return (
                    <span className="rfq_status_cell" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: statusMeta.color }}>
                        <StatusIcon />
                        <span>{statusMeta.label}</span>
                    </span>
                );
            },
            sortable: false,
            tableColumn: true,
            filterName: "statusFilter",
            type: "text",
            options: PO_STATUS_FILTER_OPTIONS,
            width: "180px",
        },
        {
            name: "Payment Status",
            selector: (row) => normalizePaymentStatus(row.payment_status),
            cell: (row) => {
                const paymentMeta = getPaymentStatusMeta(row.payment_status);
                return (
                    <span
                        className="rfq_approval"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            color: paymentMeta.color,
                        }}
                    >
                        {paymentMeta.icon && (
                            <img
                                src={paymentMeta.icon}
                                alt={`${paymentMeta.label} status`}
                                style={{ width: "14px", height: "14px", objectFit: "contain", flexShrink: 0 }}
                            />
                        )}
                        <span>{paymentMeta.label}</span>
                    </span>
                );
            },
            sortable: false,
            tableColumn: true,
            filterName: "paymentStatusFilter",
            type: "text",
            options: PO_PAYMENT_FILTER_OPTIONS,
            width: "170px",
        },
        {
            name: "Created Date",
            selector: (row) => formatDateCell(row.created_at),
            sortable: false,
            tableColumn: true,
            filterName: "createdAtFilter",
            type: "select",
            filterComponent: DateFilterDropdown,
            width: "140px",
        },
        {
            name: "Last Updated",
            selector: (row) => formatDateCell(row.updated_at),
            sortable: false,
            tableColumn: true,
            filterName: "lastUpdatedFilter",
            type: "select",
            filterComponent: DateFilterDropdown,
            width: "140px",
        },
        {
            name: "Actions",
            selector: (row) => row?.id,
            cell: (row) => (
                <div className="table_action_btn">
                    <PermissionWrapper
                        module={PROCUREMENT_MODULE}
                        permission={ProcurementPermission.DeletePo}
                        mainModule={APP_MODULE}
                    >
                        <button
                            className="delete_btn"
                            title="Delete Purchase Order"
                            onClick={(event) => {
                                event.stopPropagation();
                                openDeleteModal(PO_TAB, row);
                            }}
                        >
                            <PiTrash />
                        </button>
                    </PermissionWrapper>
                </div>
            ),
            sortable: false,
            tableColumn: false,
            ignoreRowClick: true,
            width: "110px",
        },
    ]), [canViewPo, navigate, openDeleteModal]);






    const rfqColumns = useMemo(() => ([
        {
            name: "RFQ ID",
            selector: (row) => row.parts_rfq_id,
            cell: (row) => (
                <span className="rfq_id_cell">
                    {canViewRfq ? (
                        <span className="rfq_link" onClick={() => navigate(`/rfqs/details/${row.id}`)}>{row.parts_rfq_id}</span>
                    ) : (
                        <span>{row.parts_rfq_id}</span>
                    )}
                </span>
            ),
            sortable: false,
            tableColumn: true,
            filterName: "partsRfqIdFilter",
            type: "text",
            width: "140px",
        },
        {
            name: "Date Created",
            selector: (row) => formatDateCell(row.created_at),
            sortable: false,
            tableColumn: true,
            filterName: "createdAtFilter",
            type: "select",
            filterComponent: DateFilterDropdown,
            width: "140px",
        },
        {
            name: "Requester",
            selector: (row) => row.requester?.username,
            cell: (row) => (
                <span className="rfq_requester">
                    <UserAvatar username={row.requester?.username} image={row.requester?.image} size={24} fontSize="11px" />
                    {row.requester?.username}
                </span>
            ),
            sortable: false,
            tableColumn: true,
            filterName: "requesterFilter",
            type: "text",
            width: "250px",
        },
        {
            name: "Parts",
            selector: (row) => row.items?.map(i => i.part?.name).join(', '),
            cell: (row) => {
                const partNames = row.items?.map((item) => item.part?.name).filter(Boolean) || [];

                if (partNames.length === 0) {
                    return <span>-</span>;
                }

                if (partNames.length === 1) {
                    return (
                        <span className="service-task">
                            <MultiLineText text={partNames[0]} maxWidth="150px" />
                        </span>
                    );
                }

                const remainingCount = partNames.length - 1;

                return (
                    <div className="service-tasks-container">
                        <span className="first-task">
                            <MultiLineText text={partNames[0]} maxWidth="150px" />
                        </span>

                        <OverlayTrigger
                            placement="top"
                            overlay={(
                                <Tooltip>
                                    <div>
                                        {partNames.slice(1).map((partName, index) => (
                                            <div key={`${partName}-${index}`} className="tooltip_item">{partName}</div>
                                        ))}
                                    </div>
                                </Tooltip>
                            )}
                        >
                            <span className="additional-tasks">+{remainingCount}</span>
                        </OverlayTrigger>
                    </div>
                );
            },
            sortable: false,
            tableColumn: true,
            filterName: "partFilter",
            type: "text",
            width: "200px",
        },
        {
            name: "Target Delivery Date",
            selector: (row) => formatDateCell(row.target_delivery_date),
            sortable: false,
            tableColumn: true,
            filterName: "targetDeliveryDateFilter",
            type: "select",
            filterComponent: DateFilterDropdown,
            width: "190px",
        },
        {
            name: "Current Status",
            selector: (row) => normalizeRfqStatus(row.status),
            cell: (row) => {
                const statusMeta = getRfqStatusMeta(row.status);
                const StatusIcon = statusMeta.icon;
                const isApprovedBySupplyChain = isSupplyChainApproved(row.supply_chain_status);
                const canEdit = canUpdateRfq && statusMeta.value !== "closed" && isApprovedBySupplyChain;

                return (
                    <div style={{ position: "relative", display: "inline-block" }}>
                        <div
                            className={`rfq_status ${statusMeta.classKey} rfq_status_trigger`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: statusMeta.color,
                                whiteSpace: 'nowrap',
                                cursor: canEdit ? 'pointer' : 'default',
                                opacity: canEdit ? 1 : 0.6,
                                padding: '4px 8px',
                                borderRadius: '4px',
                            }}
                            onClick={canEdit ? (e) => handleStatusClick(e, row) : undefined}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', fontSize: '16px' }}><StatusIcon /></span>
                            <span>{statusMeta.label}</span>
                        </div>

                        {statusPopup.open && String(statusPopup.rowId) === String(row.id) &&
                            <StatusPopup row={row} onSelect={handleStatusSelect} />
                        }
                    </div>
                );
            },
            sortable: false,
            tableColumn: true,
            filterName: "statusFilter",
            type: "select",
            options: RFQ_STATUS_FILTER_OPTIONS,
            width: "180px",
        },
        {
            name: "Supply Chain Approval",
            selector: (row) => normalizeRfqStatus(row.supply_chain_status),
            cell: (row) => {
                const approvalMeta = getRfqApprovalMeta(row.supply_chain_status);
                const ApprovalIcon = approvalMeta.icon;
                const canEditApproval = canApproveSupplyChain;

                return (
                    <div style={{ position: "relative", display: "inline-block" }}>
                        <span
                            className={`rfq_approval ${approvalMeta.classKey} rfq_approval_trigger`}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                color: approvalMeta.color,
                                whiteSpace: "nowrap",
                                cursor: canEditApproval ? "pointer" : "default",
                                padding: "4px 8px",
                                borderRadius: "4px",
                            }}
                            onClick={canEditApproval ? (e) => handleSupplyApprovalClick(e, row) : undefined}
                        >
                            <span style={{ display: "flex", alignItems: "center", fontSize: "16px" }}><ApprovalIcon /></span>
                            <span>{approvalMeta.label}</span>
                        </span>

                        {canApproveSupplyChain && approvalPopup.open && String(approvalPopup.rowId) === String(row.id) &&
                            <ApprovalPopup row={row} onSelect={handleSupplyApprovalSelect} />
                        }
                    </div>
                );
            },
            sortable: false,
            tableColumn: true,
            filterName: "supplyChainStatusFilter",
            type: "select",
            options: RFQ_APPROVAL_FILTER_OPTIONS,
            width: "220px",
        },
        {
            name: "Bid Count",
            selector: (row) => row?.bids?.length || 0,
            sortable: false,
            tableColumn: true,
            filterName: "bidCountFilter",
            type: "number",
            width: "120px",
        },
        {
            name: "Actions",
            selector: (row) => row?.id,
            cell: (row) => (
                <div className="table_action_btn">
                    <PermissionWrapper
                        module={PROCUREMENT_MODULE}
                        permission={ProcurementPermission.DeleteRfq}
                        mainModule={APP_MODULE}
                    >
                        <button
                            className="delete_btn"
                            title="Delete RFQ"
                            onClick={(event) => {
                                event.stopPropagation();
                                openDeleteModal(RFQ_TAB, row);
                            }}
                        >
                            <PiTrash />
                        </button>
                    </PermissionWrapper>
                </div>
            ),
            sortable: false,
            tableColumn: false,
            ignoreRowClick: true,
            width: "110px",
        },
    ]), [approvalPopup.open, approvalPopup.openAbove, approvalPopup.rowId, canApproveSupplyChain, canUpdateRfq, canViewRfq, handleStatusClick, handleStatusSelect, handleSupplyApprovalClick, handleSupplyApprovalSelect, navigate, openDeleteModal, statusPopup.open, statusPopup.openAbove, statusPopup.rowId]);

    return (
        <div>
            <Breadcrumbs values={['Procurement', (activeTab === RFQ_TAB ? 'Request for Quotations (RFQs)' : 'Purchase Orders (POs)')]} />

            <div className='access_view'>
                <h2>Procurement</h2>

                <div className='services_buttons'>
                    {activeTab === RFQ_TAB ? (
                        <>
                            <ExportButton
                                type="pdf"
                                onClick={handleRfqExportPdf}
                                disabled={!hasActiveRfqFilters || rfqPdfExportLoading}
                                loading={rfqPdfExportLoading}
                                hasActiveFilters={hasActiveRfqFilters}
                            />

                            <ExportButton
                                type="excel"
                                onClick={handleRfqExportExcel}
                                disabled={!hasActiveRfqFilters || rfqExcelExportLoading}
                                loading={rfqExcelExportLoading}
                                hasActiveFilters={hasActiveRfqFilters}
                            />

                            {canCreateRfq && (
                                <PermissionWrapper
                                    module={PROCUREMENT_MODULE}
                                    permission={ProcurementPermission.CreateRfq}
                                    mainModule={APP_MODULE}
                                >
                                    <button onClick={() => navigate('/rfqs/create')}>{plusIcon} Add RFQ</button>
                                </PermissionWrapper>
                            )}
                        </>
                    ) : (
                        <>
                            <ExportButton
                                type="pdf"
                                onClick={handlePoExportPdf}
                                disabled={!hasActivePoFilters || poPdfExportLoading}
                                loading={poPdfExportLoading}
                                hasActiveFilters={hasActivePoFilters}
                            />

                            <ExportButton
                                type="excel"
                                onClick={handlePoExportExcel}
                                disabled={!hasActivePoFilters || poExcelExportLoading}
                                loading={poExcelExportLoading}
                                hasActiveFilters={hasActivePoFilters}
                            />
                        </>
                    )}
                </div>
            </div>

            <div className="px-4">
                <CustomTabs
                    tabs={tripsTabs}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                />
            </div>

            <Stats statsData={activeTab === PO_TAB ? poStatsData : procurementStatsData} />

            {activeTab === RFQ_TAB && (
                <div className="procurement_table_box">
                    <NewTableComponent
                        columns={rfqColumns}
                        data={rfqList}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        limit={limit}
                        setLimit={setLimit}
                        totalItems={totalRfqItems}
                        totalPages={totalRfqPages}
                        loading={getRFQsLoading}
                        showFilter={true}
                        showPagination={true}
                        searchFilters={searchFilters}
                        setSearchFilters={setSearchFilters}
                        onSearch={setSearchTerm}
                        searchValue={searchTerm}
                        expandableRows={true}
                        expandableRowsComponent={ExpandableBidTable}
                        expandableRowsComponentProps={{ loadingPOs, onGeneratePO: canCreatePo ? handleGeneratePO : null }}
                        expandableRowDisabled={row => !row.bids || row.bids.length === 0}
                        expandableIcon={{
                            collapsed: <IoMdArrowDropright />,
                            expanded: <IoMdArrowDropright style={{ transform: 'rotate(90deg)' }} />
                        }}
                    />
                </div>
            )}

            {activeTab === PO_TAB && (
                <div className="procurement_table_box">
                    <NewTableComponent
                        columns={poColumns}
                        data={poList}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        limit={limit}
                        setLimit={setLimit}
                        totalItems={totalPoItems}
                        totalPages={totalPoPages}
                        loading={getPurchaseOrdersLoading}
                        showFilter={true}
                        showPagination={true}
                        searchFilters={searchFilters}
                        setSearchFilters={setSearchFilters}
                        onSearch={setSearchTerm}
                        searchValue={searchTerm}
                    />
                </div>
            )}

            <DeleteModal
                open={deleteModalOpen}
                onClose={closeDeleteModal}
                onLogout={handleDeleteConfirm}
                loading={deleteTarget?.type === RFQ_TAB ? deletePartRFQLoading : deletePurchaseOrderLoading}
                topHeading={deleteTarget?.type === RFQ_TAB ? "Delete RFQ" : "Delete Purchase Order"}
                description={deleteTarget?.type === RFQ_TAB
                    ? "Are you sure you want to delete this RFQ?"
                    : "Are you sure you want to delete this Purchase Order?"}
                subDescription="This action cannot be undone."
            />

        </div>
    );
};

export default Procurement;
